import {Injectable} from '@angular/core';
import {RequestsService} from '../requests/requests.service';
import {DiagramJsonHandlerService} from '../../diagram-data/diagram-json-handler.service';
import {HttpParams} from '@angular/common/http';
import {BehaviorSubject, Observable} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {ActivatedRoute} from '@angular/router';
import {PopupManagerService} from './popup-manager.service';
import {VersionDoesNotExistPopupComponent} from '../../editor-interface/shared/versions/version-does-not-exist-popup/version-does-not-exist-popup.component';
import {CustomEncoder} from '../helpers/custom-encoder';
import {NavigationService} from '../navigation/navigation.service';
import {DIAGRAM_VERSION_STATES_ENUM} from '../../editor-interface/shared/models/diagram-version.constant';
import {RequestTask} from 'src/app/shared/models/request-task.interface';
import {DiagramVersionItem} from '../../editor-interface/sidebar/shared/models/diagram-version-item.interface';

@Injectable({
  providedIn: 'root'
})
export class VersionsService {
  constructor(
    private requestsSvc: RequestsService,
    private diagramJsonSvc: DiagramJsonHandlerService,
    private translateSvc: TranslateService,
    private route: ActivatedRoute,
    private popupSvc: PopupManagerService,
    private navSvc: NavigationService
  ) {}

  navigationByReleaseTag;
  forceDraft = false;

  currVersionSubject: BehaviorSubject<any> = new BehaviorSubject<any>('');
  currVersion$: Observable<any> = this.currVersionSubject.asObservable();

  versionsHistorySubject: BehaviorSubject<any> = new BehaviorSubject([]);
  versionsHistory$: Observable<any> = this.versionsHistorySubject.asObservable();

  versionsSubject: BehaviorSubject<any> = new BehaviorSubject([]);
  versions$: Observable<DiagramVersionItem[]> = this.versionsSubject.asObservable();

  authorisationSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
  authorisation$: Observable<boolean> = this.authorisationSubject.asObservable();

  getVersionsHistory() {
    return this.requestsSvc.addToQueue(this.taskForVersionsHistory, true).then(result => {
      this.versionsHistorySubject.next(result);

      return result;
    });
  }

  getRelease(releaseId) {
    return this.requestsSvc
      .addToQueue(this.taskForRelease(releaseId), true)
      .then(releaseInfo => releaseInfo.release);
  }

  taskForRelease(releaseId): RequestTask {
    return {
      isApi: true,
      method: 'GET',
      url: `/release/${releaseId}`
    };
  }

  get taskForVersionsHistory(): RequestTask {
    const params = new HttpParams({
      fromString: `history=${true}`,
      encoder: new CustomEncoder()
    });

    return {
      isApi: true,
      url: `/diagram/${this.diagramJsonSvc.get().diagramId}/versions`,
      method: 'GET',
      queryParams: params
    };
  }

  getVersionName(version) {
    if (version) {
      switch (version.state) {
        case DIAGRAM_VERSION_STATES_ENUM.READY:
          return this.translateSvc.instant('VERSIONING.DIAGRAM_STATE_READY');

        case DIAGRAM_VERSION_STATES_ENUM.MASTER:
          return this.translateSvc.instant('ASIDE_R.VERSION') + ' ' + version.version;

        case DIAGRAM_VERSION_STATES_ENUM.ARCHIVE:
          return this.translateSvc.instant('ASIDE_R.VERSION') + ' ' + version.version;

        case DIAGRAM_VERSION_STATES_ENUM.NEVER_PUBLISHED:
          return this.translateSvc.instant('VERSIONING.DIAGRAM_STATE_NEVER_PUBLISHED');
      }
    } else {
      return '';
    }
  }

  getVersions(diagramId?) {
    return this.requestsSvc.addToQueue(this.taskForVersions(diagramId), true).then(result => {
      this.versionsSubject.next(result);

      return result;
    });
  }

  taskForVersions(diagramId): RequestTask {
    const id = diagramId ? diagramId : this.diagramJsonSvc.get().diagramId;

    return {
      isApi: true,
      method: 'GET',
      url: `/diagram/${id}/versions`
    };
  }

  // put to use smhow after getting diagramJson
  setStartReleaseVersionName(diagramJson) {
    const state = diagramJson.state;
    const mainRelease = diagramJson.release;
    let currentReleaseId;

    if (this.navSvc.getCurrent()?.state?.includes('r_')) {
      currentReleaseId = this.navSvc.getCurrent().state.slice(2);
    }

    switch (state) {
      case DIAGRAM_VERSION_STATES_ENUM.DRAFT:
        this.currVersionSubject.next(this.translateSvc.instant('ASIDE_R.DIAGRAM_STATE_DRAFT'));
        break;

      case DIAGRAM_VERSION_STATES_ENUM.READY:
        this.currVersionSubject.next(this.translateSvc.instant('ASIDE_R.DIAGRAM_STATE_READY'));
        break;

      case DIAGRAM_VERSION_STATES_ENUM.BEING_AUTHORISED:
        this.currVersionSubject.next(
          this.translateSvc.instant('ASIDE_R.DIAGRAM_STATE_BEING_AUTHORISED')
        );
        break;

      case DIAGRAM_VERSION_STATES_ENUM.MASTER:
        if (this.navSvc.getCurrent().state === DIAGRAM_VERSION_STATES_ENUM.MASTER) {
          // this.currVersionSubject.next(this.translateSvc.instant('TOOLBAR.PUBLISHED_STATE'));
          this.currVersionSubject.next(this.getToolbarVersion(diagramJson));
        } else {
          currentReleaseId &&
            this.getRelease(currentReleaseId).then(release =>
              this.currVersionSubject.next(this.getReleaseName(release))
            );
        }

        break;

      case DIAGRAM_VERSION_STATES_ENUM.ARCHIVE:
        if (this.navigationByReleaseTag === undefined && mainRelease.id !== currentReleaseId) {
          currentReleaseId &&
            this.getRelease(currentReleaseId).then(release =>
              this.currVersionSubject.next(this.getReleaseName(release))
            );
        } else {
          this.currVersionSubject.next(this.generateVersionNumber(diagramJson));
        }
    }
  }

  generateVersionNumber(diagram) {
    if (diagram) {
      return [
        this.translateSvc.instant('ASIDE_R.VERSION_VER'),
        ' ',
        diagram.version,
        ' : ',
        this.translateSvc.instant('ASIDE_R.DIAGRAM_STATE_ARCHIVE')
      ].join('');
    } else {
      return '';
    }
  }

  getReleaseName(release) {
    if (release) {
      return (
        release.name +
        ' ( ' +
        release.number.major +
        '.' +
        release.number.minor +
        '.' +
        release.number.patch +
        ' )'
      );
    } else {
      return '';
    }
  }

  openVersionNotExistPopup(errorInfo) {
    return this.popupSvc.open(VersionDoesNotExistPopupComponent, {
      panelClass: ['q9-dialog'],
      data: errorInfo,
      disableClose: true
    });
  }

  getToolbarVersion(item) {
    if (!item.release) {
      return;
    }

    if (item.state === DIAGRAM_VERSION_STATES_ENUM.MASTER) {
      return [
        this.translateSvc.instant('ASIDE_R.PUBLISHED_LABEL'),
        ':',
        this.translateSvc.instant('ASIDE_R.VERSION_VER'),
        item.version
      ].join(' ');
    } else if (item.state === DIAGRAM_VERSION_STATES_ENUM.ARCHIVE) {
      return [
        this.translateSvc.instant('ASIDE_R.VERSION_VER'),
        item.version,
        ':',
        this.translateSvc.instant('ASIDE_R.DIAGRAM_STATE_ARCHIVE')
      ].join(' ');
    }
  }

  getMapAuthorisationMode() {
    return this.requestsSvc
      .addToQueue(this.authorisationModeTask, true)
      .then(({isAuthorisationEnabled}) => this.authorisationSubject.next(isAuthorisationEnabled));
  }

  get authorisationModeTask(): RequestTask {
    return {
      isApi: true,
      url: `/map/${this.diagramJsonSvc.get().map.id}/authorisation`,
      method: 'GET'
    };
  }
}
